import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  CalendarDays,
  Users,
  Trophy,
  Newspaper,
  Pencil,
  Clock,
  MapPin,
  Sparkles,
  Home,
  PlayCircle,
  Hourglass,
  Info,
  RefreshCw,
} from "lucide-react";
import { useTranslation as useTranslationOrig } from "react-i18next";

// --- i18n safe (t + language)
type RawT = (key: string, options?: Record<string, unknown>) => any;
const useTranslationSafe = (ns?: string) =>
  (useTranslationOrig as unknown as (ns?: any) => {
    t: RawT;
    i18n: { language: string };
  })(ns);

// —— Types ——
export type Tournament = {
  id: string;
  user_id: string;
  name: string;
  location?: string | null;
  start_date?: string | null; // ISO date (YYYY-MM-DD)
  end_date?: string | null; // ISO date
  is_public: boolean;
  rules_note?: string | null;
};

export type Team = {
  id: string;
  tournament_id: string;
  name: string;
  short_name?: string | null;
  logo_path?: string | null; // storage path
  group_name?: string | null;
};

type MatchStatus = "programmé" | "live" | "terminé" | "scheduled" | "finished";

export type Match = {
  id: string;
  tournament_id: string;
  start_time: string; // ISO datetime
  court?: string | null;
  status: MatchStatus;
  team_home: string;
  team_away: string;
  score_home?: number | null;
  score_away?: number | null;
  table_de_marque?: string | null;
  arbitrage?: string | null;
};

export type News = {
  id: string;
  title: string;
  content: string;
  created_at: string;
};

// —— Helpers i18n ——
// Normalise vers 3 statuts clés, quelle que soit la langue stockée en BDD
function statusKey(status?: string): "scheduled" | "live" | "finished" {
  switch ((status || "").toLowerCase()) {
    case "live":
      return "live";
    case "finished":
    case "terminé":
      return "finished";
    case "scheduled":
    case "programmé":
    default:
      return "scheduled";
  }
}

// URL signée pour les logos d’équipes
async function getSignedLogoUrl(path?: string | null): Promise<string | null> {
  if (!path) return null;
  const { data, error } = await supabase.storage
    .from("tournament-logos")
    .createSignedUrl(path, 3600);
  if (error) return null;
  return data?.signedUrl ?? null;
}

const IMAGE_MARKDOWN_RE = /!\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)/g;

function renderNewsContent(content: string) {
  const parts: JSX.Element[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;

  while ((match = IMAGE_MARKDOWN_RE.exec(content)) !== null) {
    const [fullMatch, alt, src] = match;
    const start = match.index;

    if (start > lastIndex) {
      const textChunk = content.slice(lastIndex, start);
      if (textChunk.trim() || textChunk.includes("\n")) {
        parts.push(
          <div
            key={`text-${i}`}
            className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300"
          >
            {textChunk}
          </div>
        );
        i++;
      }
    }

    parts.push(
      <div key={`img-${i}`} className="my-3">
        <img
          src={src}
          alt={alt || "news image"}
          className="max-h-[420px] w-auto max-w-full rounded-xl border border-gray-200 dark:border-gray-800 object-contain"
          loading="lazy"
        />
        {alt ? (
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{alt}</div>
        ) : null}
      </div>
    );
    i++;

    lastIndex = start + fullMatch.length;
  }

  if (lastIndex < content.length) {
    const tail = content.slice(lastIndex);
    if (tail.trim() || tail.includes("\n")) {
      parts.push(
        <div
          key={`text-tail-${i}`}
          className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300"
        >
          {tail}
        </div>
      );
    }
  }

  if (parts.length === 0) {
    return (
      <div className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
        {content}
      </div>
    );
  }

  return <div className="space-y-2">{parts}</div>;
}

export const TournamentPage = () => {
  const { id } = useParams();
  const { t: tRaw, i18n } = useTranslationSafe("tournament");
  const tr = (key: string, options?: Record<string, unknown>): string => {
    const out = tRaw(key, options);
    if (typeof out === "string") return out;
    if (Array.isArray(out)) return out.join("");
    return String(out ?? "");
  };
  const lang = i18n?.language || "fr";

  // Format dates selon la langue
  const fmtDate = (iso?: string | null) =>
    iso
      ? new Intl.DateTimeFormat(lang, { dateStyle: "medium" }).format(new Date(iso))
      : tr("common.tbd");

  const fmtDateTime = (iso?: string | null) =>
    iso
      ? new Intl.DateTimeFormat(lang, { dateStyle: "short", timeStyle: "short" }).format(
          new Date(iso)
        )
      : tr("common.tbd");

  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [tab, setTab] = useState<"accueil" | "planning" | "equipes" | "resultats" | "news">(
    "accueil"
  );

  const [teams, setTeams] = useState<Team[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [news, setNews] = useState<News[]>([]);
  const [logoUrls, setLogoUrls] = useState<Record<string, string | null>>({});
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Accueil : règles
  const [rulesNote, setRulesNote] = useState<string | null>(null);

  const liveMatches = useMemo(
    () =>
      matches
        .filter((m) => statusKey(m.status) === "live")
        .sort((a, b) => a.start_time.localeCompare(b.start_time)),
    [matches]
  );

  const latestNews = useMemo(() => (news.length > 0 ? news[0] : null), [news]);

  const teamsById = useMemo(
    () => Object.fromEntries(teams.map((t) => [t.id, t] as const)),
    [teams]
  );

  const standingsByGroup = useMemo(() => {
    const finishedMatches = matches.filter((m) => statusKey(m.status) === "finished");

    const table: Record<
      string,
      Record<
        string,
        {
          teamId: string;
          teamName: string;
          played: number;
          wins: number;
          losses: number;
          scored: number;
          conceded: number;
          diff: number;
        }
      >
    > = {};

    finishedMatches.forEach((m) => {
      const homeTeam = teamsById[m.team_home];
      const awayTeam = teamsById[m.team_away];

      if (!homeTeam || !awayTeam) return;

      const homeGroup = homeTeam.group_name || "Sans poule";
      const awayGroup = awayTeam.group_name || "Sans poule";

      if (homeGroup !== awayGroup) return;

      if (!table[homeGroup]) table[homeGroup] = {};

      const ensure = (team: Team) => {
        if (!table[homeGroup][team.id]) {
          table[homeGroup][team.id] = {
            teamId: team.id,
            teamName: team.name,
            played: 0,
            wins: 0,
            losses: 0,
            scored: 0,
            conceded: 0,
            diff: 0,
          };
        }
        return table[homeGroup][team.id];
      };

      const home = ensure(homeTeam);
      const away = ensure(awayTeam);

      const hs = m.score_home ?? 0;
      const as = m.score_away ?? 0;

      home.played++;
      away.played++;

      home.scored += hs;
      home.conceded += as;
      home.diff = home.scored - home.conceded;

      away.scored += as;
      away.conceded += hs;
      away.diff = away.scored - away.conceded;

      if (hs > as) {
        home.wins++;
        away.losses++;
      } else if (as > hs) {
        away.wins++;
        home.losses++;
      }
    });

    const result: Record<
      string,
      {
        teamId: string;
        teamName: string;
        played: number;
        wins: number;
        losses: number;
        scored: number;
        conceded: number;
        diff: number;
      }[]
    > = {};

    Object.keys(table).forEach((group) => {
      result[group] = Object.values(table[group]).sort((a, b) => {
        if (b.wins !== a.wins) return b.wins - a.wins;
        if (b.diff !== a.diff) return b.diff - a.diff;
        return b.scored - a.scored;
      });
    });

    return result;
  }, [matches, teamsById]);

  // Libellé équipe
  const teamLabel = (val?: string | null) => {
    if (!val) return tr("teams.unknown");
    const team = teamsById[val];
    return team?.name || val;
  };

  // Libellé équipe court (pour les prochains matchs)
  const teamShortLabel = (val?: string | null) => {
    if (!val) return tr("teams.unknown");
    const team = teamsById[val];
    return team?.short_name?.trim() || team?.name || val;
  };

  // Mini chip avec logo
  const TeamChip = ({
    value,
    short = false,
  }: {
    value?: string | null;
    short?: boolean;
  }) => {
    const team = value ? teamsById[value] : undefined;
    const label = short ? teamShortLabel(value) : teamLabel(value);
    const logo = team ? logoUrls[team.id] : null;

    return (
      <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950">
        {logo ? (
          <img src={logo} alt={label} className="w-5 h-5 rounded object-cover" />
        ) : (
          <span className="w-5 h-5 rounded bg-gray-200 dark:bg-gray-800" />
        )}
        <span className="font-medium">{label}</span>
      </span>
    );
  };

  // Rendu A vs B
  const Versus = ({
    home,
    away,
    short = false,
  }: {
    home?: string | null;
    away?: string | null;
    short?: boolean;
  }) => (
    <div className="flex items-center gap-2 flex-wrap">
      <TeamChip value={home} short={short} />
      <span className="text-xs text-gray-500 dark:text-gray-400">{tr("common.vs")}</span>
      <TeamChip value={away} short={short} />
    </div>
  );

  // Prochains matchs par terrain (le plus proche futur)
  const nextByCourt = useMemo(() => {
    const nowMs = Date.now();
    const best: Record<string, Match> = {};

    for (const m of matches) {
      if (statusKey(m.status) !== "scheduled") continue;
      const startMs = new Date(m.start_time).getTime();
      if (startMs <= nowMs) continue;
      const court = m.court || "—";
      const current = best[court];
      if (!current || new Date(current.start_time).getTime() > startMs) {
        best[court] = m;
      }
    }

    return Object.values(best).sort(
      (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
    );
  }, [matches]);

  const loadTournamentData = useCallback(async (showRefreshing = false) => {
    if (!id) return;

    if (showRefreshing) setRefreshing(true);
    else setLoading(true);

    const [{ data: t, error: tErr }, { data: tm }, { data: te }, { data: nw }] =
      await Promise.all([
        supabase
          .from("tournaments")
          .select("id,user_id,name,location,start_date,end_date,is_public,rules_note")
          .eq("id", id)
          .maybeSingle(),
        supabase
          .from("tournament_matches")
          .select(
            "id,tournament_id,start_time,court,status,team_home,team_away,score_home,score_away,table_de_marque,arbitrage"
          )
          .eq("tournament_id", id)
          .order("start_time", { ascending: true }),
        supabase
          .from("tournament_teams")
          .select("id,tournament_id,name,short_name,logo_path,group_name")
          .eq("tournament_id", id)
          .order("name"),
        supabase
          .from("tournament_news")
          .select("id,title,content,created_at")
          .eq("tournament_id", id)
          .order("created_at", { ascending: false }),
      ]);

    if (tErr) {
      setTournament(null);
      setLoading(false);
      setRefreshing(false);
      return;
    }

    if (t) {
      setTournament(t as Tournament);
      setRulesNote((t as Tournament).rules_note ?? null);
    }

    setMatches((tm as Match[]) || []);
    setTeams((te as Team[]) || []);
    setNews((nw as News[]) || []);

const { data: auth } = await supabase.auth.getUser();

let canManage = false;

if (auth?.user && t) {
  const tournamentData = t as Tournament;
  const isOwner = auth.user.id === tournamentData.user_id;

  if (isOwner) {
    canManage = true;
  } else {
    const { data: adminRow } = await supabase
      .from("tournament_admins")
      .select("tournament_id")
      .eq("tournament_id", tournamentData.id)
      .eq("user_id", auth.user.id)
      .maybeSingle();

    canManage = Boolean(adminRow);
  }
}

setIsOwner(canManage);

    const urls: Record<string, string | null> = {};
    await Promise.all(
      ((te as Team[]) || []).map(async (team) => {
        urls[team.id] = await getSignedLogoUrl(team.logo_path);
      })
    );
    setLogoUrls(urls);

    setLoading(false);
    setRefreshing(false);
  }, [id]);

  useEffect(() => {
    loadTournamentData();
  }, [loadTournamentData]);

  if (!id) {
    return <div className="p-4 text-gray-700 dark:text-gray-200">{tr("state.missingId")}</div>;
  }

  if (loading) {
    return <div className="p-6 text-gray-700 dark:text-gray-200">{tr("state.loading")}</div>;
  }

  if (!tournament) {
    return <div className="p-6 text-gray-700 dark:text-gray-200">{tr("state.notFound")}</div>;
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 text-gray-900 dark:text-gray-100">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Sparkles className="w-5 h-5" /> {tournament.name}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 flex flex-wrap items-center gap-2 mt-1">
            <MapPin className="w-4 h-4" /> {tournament.location ?? tr("header.locationTbd")}
            <span>•</span>
            <Clock className="w-4 h-4" />
            {tournament.start_date ? fmtDate(tournament.start_date) : tr("header.datesTbd")}
            {tournament.end_date ? ` → ${fmtDate(tournament.end_date)}` : ""}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => loadTournamentData(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-2 rounded-xl px-3 py-2 border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 hover:bg-gray-50 dark:hover:bg-gray-900 transition disabled:opacity-60"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing
              ? tr("common.refreshing", { defaultValue: "Actualisation..." })
              : tr("common.refresh", { defaultValue: "Rafraîchir" })}
          </button>

          {isOwner && (
            <Link
              to={`/tournaments/${id}/admin`}
              className="inline-flex items-center gap-2 rounded-xl px-3 py-2 border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 hover:bg-gray-50 dark:hover:bg-gray-900 transition"
            >
              <Pencil className="w-4 h-4" /> {tr("header.manage")}
            </Link>
          )}
        </div>
      </header>

      {/* Onglets */}
      <div className="flex flex-wrap gap-2 mb-4">
        {([
          ["accueil", <Home key="h" className="w-4 h-4" />, tr("tabs.home")],
          ["planning", <CalendarDays key="c" className="w-4 h-4" />, tr("tabs.schedule")],
          ["equipes", <Users key="u" className="w-4 h-4" />, tr("tabs.teams")],
          ["resultats", <Trophy key="t" className="w-4 h-4" />, tr("tabs.results")],
          ["news", <Newspaper key="n" className="w-4 h-4" />, tr("tabs.news")],
        ] as const).map(([key, icon, label]) => (
          <button
            key={key}
            onClick={() => setTab(key as typeof tab)}
            className={[
              "inline-flex items-center gap-2 px-3 py-2 rounded-xl border transition",
              "border-gray-200 dark:border-gray-800",
              tab === key
                ? "bg-gray-100 dark:bg-gray-900"
                : "bg-white dark:bg-gray-950 hover:bg-gray-50 dark:hover:bg-gray-900",
            ].join(" ")}
          >
            {icon} {label}
          </button>
        ))}
      </div>

      {/* ====== ACCUEIL ====== */}
      {tab === "accueil" && (
        <div className="space-y-6">
          <section className="rounded-2xl border border-gray-200 dark:border-gray-800 p-4 bg-white dark:bg-gray-950">
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <PlayCircle className="w-5 h-5 text-red-600" />
              {tr("home.liveMatches.title")}
            </h2>

            {liveMatches.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {tr("home.liveMatches.empty")}
              </p>
            ) : (
              <div className="grid md:grid-cols-2 gap-3">
                {liveMatches.map((m) => (
                  <div
                    key={m.id}
                    className="rounded-xl border border-gray-200 dark:border-gray-800 p-3 bg-white dark:bg-gray-950"
                  >
                    <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                      <div className="inline-flex items-center gap-1">
                        <Versus home={m.team_home} away={m.team_away} />
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                      <div className="text-sm text-gray-700 dark:text-gray-300">
                        {tr("home.liveMatches.court")}:{" "}
                        <span className="font-medium">{m.court ?? "—"}</span>
                      </div>

                      <div className="rounded-xl border border-red-200 dark:border-red-900/60 bg-red-50 dark:bg-red-950/30 px-4 py-2">
                        <div className="text-xs uppercase tracking-wide text-red-600 dark:text-red-400 font-semibold">
                          {tr("common.live", { defaultValue: "Live" })}
                        </div>
                        <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {m.score_home ?? 0} - {m.score_away ?? 0}
                        </div>
                      </div>
                    </div>

                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      {tr("home.liveMatches.start")}: {fmtDateTime(m.start_time)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-gray-200 dark:border-gray-800 p-4 bg-white dark:bg-gray-950">
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Hourglass className="w-5 h-5 text-amber-600" />
              {tr("home.next.title")}
            </h2>

            {nextByCourt.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">{tr("home.next.empty")}</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900 text-left">
                    <tr className="border-b border-gray-200 dark:border-gray-800">
                      <th className="py-2 px-3">{tr("home.next.th.time")}</th>
                      <th className="py-2 px-3">{tr("home.next.th.court")}</th>
                      <th className="py-2 px-3">{tr("home.next.th.match")}</th>
                      <th className="py-2 px-3">{tr("home.next.th.status")}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-950">
                    {nextByCourt.map((m) => (
                      <tr
                        key={m.id}
                        className="border-b last:border-0 border-gray-200 dark:border-gray-800 align-top"
                      >
                        <td className="py-2 px-3 whitespace-nowrap text-gray-700 dark:text-gray-300">
                          {fmtDateTime(m.start_time)}
                        </td>
                        <td className="py-2 px-3 text-gray-700 dark:text-gray-300">
                          {m.court ?? "—"}
                        </td>
                        <td className="py-2 px-3">
                          <Versus home={m.team_home} away={m.team_away} short />
                        </td>
                        <td className="py-2 px-3">
                          <span className="inline-flex px-2 py-1 rounded-full text-xs border border-amber-500/40 bg-amber-500/10 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300">
                            {tr(`status.${statusKey(m.status)}`)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-gray-200 dark:border-gray-800 p-4 bg-white dark:bg-gray-950">
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Info className="w-5 h-5 text-blue-600" />
              {tr("home.rules.title")}
            </h2>

            {rulesNote && rulesNote.trim() !== "" ? (
              <div className="prose dark:prose-invert max-w-none text-sm leading-relaxed whitespace-pre-wrap">
                {rulesNote}
              </div>
            ) : (
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {tr("home.rules.empty.intro")} <code>rules_note</code> (
                {tr("home.rules.empty.type")}) {tr("home.rules.empty.in")}{" "}
                <code>tournaments</code> {tr("home.rules.empty.hint")}
                <ul className="list-disc pl-6 mt-2">
                  <li>{tr("home.rules.empty.items.periods")}</li>
                  <li>{tr("home.rules.empty.items.fouls")}</li>
                  <li>{tr("home.rules.empty.items.timeouts")}</li>
                  <li>{tr("home.rules.empty.items.ranking")}</li>
                  <li>{tr("home.rules.empty.items.tableRef")}</li>
                </ul>
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-gray-200 dark:border-gray-800 p-4 bg-white dark:bg-gray-950">
            <div className="flex items-center justify-between gap-3 mb-3">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Newspaper className="w-5 h-5 text-emerald-600" />
                {tr("home.latestNews.title", { defaultValue: "Dernière news" })}
              </h2>

              {latestNews ? (
                <button
                  onClick={() => setTab("news")}
                  className="text-sm text-blue-600 hover:underline"
                >
                  {tr("home.latestNews.viewAll", { defaultValue: "Voir toutes les news" })}
                </button>
              ) : null}
            </div>

            {!latestNews ? (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {tr("news.empty")}
              </div>
            ) : (
              <article>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {fmtDateTime(latestNews.created_at)}
                </div>
                <h3 className="font-semibold text-lg mt-1">{latestNews.title}</h3>
                <div className="mt-2">{renderNewsContent(latestNews.content)}</div>
              </article>
            )}
          </section>
        </div>
      )}

      {/* ====== PLANNING ====== */}
      {tab === "planning" && (
        <section className="space-y-3">
          {matches.length === 0 ? (
            <div className="text-sm text-gray-500 dark:text-gray-400">{tr("planning.empty")}</div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950">
              <table className="min-w-full text-sm">
                <thead className="text-left bg-gray-50 dark:bg-gray-900">
                  <tr className="border-b border-gray-200 dark:border-gray-800">
                    <th className="py-2 pr-3">{tr("planning.th.datetime")}</th>
                    <th className="py-2 pr-3">{tr("planning.th.match")}</th>
                    <th className="py-2 pr-3">{tr("planning.th.court")}</th>
                    <th className="py-2 pr-3">{tr("planning.th.table")}</th>
                    <th className="py-2 pr-3">{tr("planning.th.referees")}</th>
                    <th className="py-2 pr-3">{tr("planning.th.status")}</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-950">
                  {matches.map((m) => (
                    <tr
                      key={m.id}
                      className="border-b last:border-0 border-gray-200 dark:border-gray-800 align-top"
                    >
                      <td className="py-2 pr-3 whitespace-nowrap text-gray-700 dark:text-gray-300">
                        {fmtDateTime(m.start_time)}
                      </td>
                      <td className="py-2 pr-3">
                        <Versus home={m.team_home} away={m.team_away} />
                      </td>
                      <td className="py-2 pr-3 text-gray-700 dark:text-gray-300">
                        {m.court ?? "-"}
                      </td>
                      <td className="py-2 pr-3 text-gray-700 dark:text-gray-300">
                        {m.table_de_marque || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="py-2 pr-3 text-gray-700 dark:text-gray-300">
                        {m.arbitrage || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="py-2 pr-3 capitalize text-gray-700 dark:text-gray-300">
                        {tr(`status.${statusKey(m.status)}`)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* ====== ÉQUIPES ====== */}
      {tab === "equipes" && (
        <section className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {teams.map((team) => (
            <div
              key={team.id}
              className="rounded-xl border border-gray-200 dark:border-gray-800 p-3 bg-white dark:bg-gray-950 flex items-center gap-3"
            >
              {logoUrls[team.id] ? (
                <img
                  src={logoUrls[team.id] as string}
                  alt={tr("teams.logoAlt", { team: team.name })}
                  className="w-10 h-10 rounded-lg object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-gray-200 dark:bg-gray-800" />
              )}

              <div>
                <div className="font-semibold">{team.name}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {team.group_name ?? "—"}
                </div>
              </div>
            </div>
          ))}

          {teams.length === 0 && (
            <div className="text-sm text-gray-500 dark:text-gray-400">{tr("teams.empty")}</div>
          )}
        </section>
      )}

      {/* ====== RÉSULTATS ====== */}
      {tab === "resultats" && (
        <section className="space-y-6">
          {Object.keys(standingsByGroup).length > 0 && (
            <div className="space-y-4">
              {Object.entries(standingsByGroup).map(([group, rows]) => (
                <div
                  key={group}
                  className="rounded-2xl border border-gray-200 dark:border-gray-800 p-4 bg-white dark:bg-gray-950"
                >
                  <h2 className="text-lg font-semibold mb-3">
                    {tr("results.standings.group", { defaultValue: "Classement poule" })} {group}
                  </h2>

                  <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
                    <table className="min-w-full text-sm">
                      <thead className="text-left bg-gray-50 dark:bg-gray-900">
                        <tr className="border-b border-gray-200 dark:border-gray-800">
                          <th className="py-2 px-3">#</th>
                          <th className="py-2 px-3">
                            {tr("results.standings.team", { defaultValue: "Équipe" })}
                          </th>
                          <th className="py-2 px-3">J</th>
                          <th className="py-2 px-3">V</th>
                          <th className="py-2 px-3">D</th>
                          <th className="py-2 px-3">+</th>
                          <th className="py-2 px-3">-</th>
                          <th className="py-2 px-3">
                            {tr("results.standings.diff", { defaultValue: "Diff" })}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-950">
                        {rows.map((r, index) => (
                          <tr
                            key={r.teamId}
                            className="border-b last:border-0 border-gray-200 dark:border-gray-800"
                          >
                            <td className="py-2 px-3 font-semibold">{index + 1}</td>
                            <td className="py-2 px-3">{r.teamName}</td>
                            <td className="py-2 px-3">{r.played}</td>
                            <td className="py-2 px-3">{r.wins}</td>
                            <td className="py-2 px-3">{r.losses}</td>
                            <td className="py-2 px-3">{r.scored}</td>
                            <td className="py-2 px-3">{r.conceded}</td>
                            <td className="py-2 px-3 font-semibold">
                              {r.diff > 0 ? `+${r.diff}` : r.diff}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}

          {matches.filter((m) => statusKey(m.status) === "finished").length === 0 ? (
            <div className="text-sm text-gray-500 dark:text-gray-400">{tr("results.empty")}</div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950">
              <table className="min-w-full text-sm">
                <thead className="text-left bg-gray-50 dark:bg-gray-900">
                  <tr className="border-b border-gray-200 dark:border-gray-800">
                    <th className="py-2 pr-3">{tr("results.th.date")}</th>
                    <th className="py-2 pr-3">{tr("results.th.match")}</th>
                    <th className="py-2 pr-3">{tr("results.th.score")}</th>
                    <th className="py-2 pr-3">{tr("results.th.details")}</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-950">
                  {matches
                    .filter((m) => statusKey(m.status) === "finished")
                    .map((m) => (
                      <tr
                        key={m.id}
                        className="border-b last:border-0 border-gray-200 dark:border-gray-800 align-top"
                      >
                        <td className="py-2 pr-3 whitespace-nowrap text-gray-700 dark:text-gray-300">
                          {fmtDateTime(m.start_time)}
                        </td>
                        <td className="py-2 pr-3">
                          <Versus home={m.team_home} away={m.team_away} />
                        </td>
                        <td className="py-2 pr-3 font-semibold">
                          {m.score_home ?? "-"} - {m.score_away ?? "-"}
                        </td>
                        <td className="py-2 pr-3">
                          <div className="text-xs">
                            <div className="text-gray-700 dark:text-gray-300">
                              <span className="text-gray-500 dark:text-gray-400">
                                {tr("results.details.table")} :
                              </span>{" "}
                              {m.table_de_marque || <span className="text-gray-400">—</span>}
                            </div>
                            <div className="text-gray-700 dark:text-gray-300">
                              <span className="text-gray-500 dark:text-gray-400">
                                {tr("results.details.referees")} :
                              </span>{" "}
                              {m.arbitrage || <span className="text-gray-400">—</span>}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* ====== NEWS ====== */}
      {tab === "news" && (
        <section className="space-y-3">
          {news.length === 0 ? (
            <div className="text-sm text-gray-500 dark:text-gray-400">{tr("news.empty")}</div>
          ) : (
            <ul className="space-y-3">
              {news.map((n) => (
                <li
                  key={n.id}
                  className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 bg-white dark:bg-gray-950"
                >
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {fmtDateTime(n.created_at)}
                  </div>
                  <h3 className="font-semibold text-lg mt-1">{n.title}</h3>
                  <div className="mt-2">{renderNewsContent(n.content)}</div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
};